import type { UIMessage } from 'ai';
import type Stripe from 'stripe';
import { filterMessages } from './filter-messages';
import type { StripeIntegration } from '~/lib/.server/llm/providers/types';
import type { FrameworkName } from '~/lib/persistence/frameworks';
import type { PromptMode, StripeStatus } from '~/lib/prompts/types';
import type { FileMap } from '~/lib/stores/files';
import type { ChatRequestBody } from '~/routes/api.chat';
import type { Message } from '~/types/chat';
import type { FeaturePreviews } from '~/types/feature-previews';
import { redactActionsContent } from '~/utils/artifacts';
import { fileSelectionsToXML } from '~/utils/file-selections';
import { getFocusedFiles } from '~/utils/focused-files';
import { getInspectedElement } from '~/utils/inspect-element';
import { invariant } from '~/utils/invariant';
import { createScopedLogger } from '~/utils/logger';
import { injectProductInfo, isStripeConfiguredInThisApp } from '~/utils/stripe-helpers';

// we only sent the previous 5 user messages plus the current one as context to the LLM
const NUMBER_OF_USER_MESSAGES_AS_CONTEXT = 6;

const logger = createScopedLogger('prepareBody');

interface AdditionalContext {
  fileSelections?: string;
  focusedFiles?: string;
  inspectedElement?: string;
  stripeProductInfo?: string;
  supportIntegrations?: boolean;
  attachments?: string[];
  hostingProvider?: 'bolt' | 'netlify';
}

interface Options {
  messages: UIMessage[];
  promptMode: PromptMode;
  stripeConfigured: boolean;
  currentFiles: FileMap;
  projectFiles: ChatRequestBody['projectFiles'];
  globalSystemPrompt: string | undefined;
  projectPrompt: string | undefined;
  supportIntegrations: boolean;
  runningCommands: string[];
  framework: FrameworkName | undefined;
  products: Stripe.Product[];
  hasEdgeFunctions: boolean;
  supabaseProjectId: string | undefined;
  projectId: string | undefined;
  featurePreviews?: Partial<FeaturePreviews> | undefined;
  dependencies: string[];
  hostingProvider?: 'bolt' | 'netlify';
}

type SerializedMessage = Pick<
  Message,
  'id' | 'content' | 'role' | 'data' | 'annotations' | 'toolInvocations' | 'parts' | 'experimental_attachments'
> & {
  rawContent?: string;
  cache: boolean;
  stripeIntegration?: StripeIntegration;
  details?: Record<string, unknown>;
};

export function updateMessages(options: Options) {
  const {
    currentFiles,
    projectFiles,
    framework,
    products,
    promptMode,
    hasEdgeFunctions,
    runningCommands,
    stripeConfigured,
    supportIntegrations,
    supabaseProjectId,
    projectId,
    featurePreviews,
    globalSystemPrompt,
    projectPrompt,
    dependencies,
    hostingProvider,
  } = options;

  const typedMessages = options.messages as Array<Readonly<Message>>;

  const isFirstPrompt = !typedMessages.some(({ role, status }) => role === 'assistant' && status === 'complete');

  const latestMessage = typedMessages.at(-1);

  invariant(latestMessage, 'Expected at least one message');

  const isErrorMode =
    latestMessage.type === 'problems' && latestMessage.resolved === true && (!promptMode || promptMode === 'build');

  const filteredMessages = filterMessages(
    typedMessages,
    NUMBER_OF_USER_MESSAGES_AS_CONTEXT /* size of sliding context window */,
  );

  const messages: any[] = [];

  let additionalContext: AdditionalContext = {};

  for (let index = 0; index < filteredMessages.length; index++) {
    const message = filteredMessages[index];

    const isLastMessage = index === filteredMessages.length - 1;
    const isUserMessage = message.role === 'user';
    const isAssistantMessage = message.role === 'assistant';
    const isChatMessage = message.type === 'chat';

    let rawContent;
    let { content, parts } = message;

    if (isUserMessage) {
      content = content.trim();

      if (isChatMessage) {
        rawContent = message.input;
      }

      if (isChatMessage && isLastMessage) {
        additionalContext = {
          fileSelections: fileSelectionsToXML(message.fileSelections),
          focusedFiles: getFocusedFiles(message.focusedFiles),
          inspectedElement: getInspectedElement(message.inspectedElement),
          stripeProductInfo: injectProductInfo(products, stripeConfigured, rawContent),
          supportIntegrations,
          attachments: message.experimental_attachments?.flatMap(({ name }) => name || []),
        };
      }
    }

    const attachments = message.experimental_attachments?.length
      ? message.experimental_attachments.map((attachment) => {
          const isSVG = attachment.contentType?.includes('image/svg');

          return {
            ...attachment,

            // claude API workaround - errors on SVG images, but works fine when they are treated as text
            contentType: isSVG ? 'text/plain' : attachment.contentType,
          };
        })
      : undefined;

    if (isAssistantMessage) {
      const { newContent, newParts } = redactActionsContent(content, message.parts, '(Content omitted)');
      content = newContent;
      parts = newParts;
    }

    /**
     * @see https://github.com/vercel/ai/blob/17a3cd0965731e94ff84569d447894dd08dc785b/packages/react/src/use-chat.ts#L264-L286
     */
    messages.push({
      id: message.id,
      role: message.role,
      stripeIntegration: extractStripeIntegration(message),
      content,
      rawContent,
      details: 'details' in message ? message.details : undefined,
      cache: false,
      experimental_attachments: attachments,
      data: message.data,
      annotations: message.annotations,
      toolInvocations: message.toolInvocations,
      parts,
    } satisfies SerializedMessage);
  }

  if (import.meta.env.DEV) {
    logger.debug(messages);
  }

  let stripeStatus: StripeStatus;

  // stripe is configured if we have their api key which means it was setup within bolt
  if (stripeConfigured) {
    if (hasEdgeFunctions) {
      stripeStatus = 'configured';
    } else {
      stripeStatus = 'needs-functions';
    }
  } else {
    if (isStripeConfiguredInThisApp(currentFiles)) {
      stripeStatus = 'user-configured';
    } else {
      stripeStatus = 'not-configured';
    }
  }

  return {
    messages,
    isFirstPrompt,
    featurePreviews,
    errorReasoning: isErrorMode ? latestMessage.reasoningData : null,
    framework,
    promptMode,
    projectId,
    supabaseProjectId,
    stripeStatus,
    usesInspectedElement: Boolean(additionalContext.inspectedElement),
    supportIntegrations: additionalContext.supportIntegrations,
    fileSelections: additionalContext.fileSelections,
    focusedFiles: additionalContext.focusedFiles,
    inspectedElement: additionalContext.inspectedElement,
    stripeProductInfo: additionalContext.stripeProductInfo,
    runningCommands,
    projectFiles,
    globalSystemPrompt,
    projectPrompt,
    dependencies,
    hostingProvider,
  } satisfies ChatRequestBody;
}

function extractStripeIntegration(message: Message) {
  return message.role === 'user' && message.type === 'chat' ? message.stripeIntegration : undefined;
}
